import { getAppName } from '../../../utils/getAppName';
import { GetAuth0SessionResponse } from '../../types';

export const SSO_MESSAGE_TYPE = 'ross-auth:sso-check';

type SsoCheckMessage = {
  type: typeof SSO_MESSAGE_TYPE;
  status: 'authenticated' | 'unauthenticated' | 'error';
  isAuthenticated: boolean | null;
};

type CheckSsoOptions = {
  authServiceBaseUrl: string;
  retailUnit: string;
  lang: string;
  timeoutMs?: number;
};

let activeSsoCheck: Promise<boolean> | undefined;

export function checkSsoInIframe({
  authServiceBaseUrl,
  retailUnit,
  lang,
  timeoutMs = 10_000,
}: CheckSsoOptions): Promise<boolean> {
  if (activeSsoCheck) return activeSsoCheck;

  activeSsoCheck = new Promise<boolean>((resolve, reject) => {
    const iframe = document.createElement('iframe');
    const authServiceOrigin = new URL(authServiceBaseUrl).origin;
    const authServiceRoot = `${authServiceBaseUrl.replace(/\/$/, '')}/`;
    const silentLoginUrl = new URL('silent-login', authServiceRoot);

    silentLoginUrl.searchParams.set('retailUnit', retailUnit);
    silentLoginUrl.searchParams.set('lang', lang);
    silentLoginUrl.searchParams.set('iframeOrigin', globalThis.location.origin);

    iframe.hidden = true;
    iframe.title = 'Authentication status check';

    const cleanup = () => {
      globalThis.clearTimeout(timeout);
      globalThis.removeEventListener('message', onMessage);
      iframe.remove();
      activeSsoCheck = undefined;
    };

    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== authServiceOrigin || event.source !== iframe.contentWindow) {
        return;
      }

      const message = event.data as Partial<SsoCheckMessage>;
      if (message.type !== SSO_MESSAGE_TYPE) return;
      if (!['authenticated', 'unauthenticated', 'error'].includes(message.status ?? '')) return;

      cleanup();

      if (message.status === 'error') {
        reject(new Error('Auth0 SSO status is temporarily unavailable'));
        return;
      }

      resolve(message.status === 'authenticated' && message.isAuthenticated === true);
    };

    const timeout = globalThis.setTimeout(() => {
      cleanup();
      reject(new Error('Auth0 SSO status check timed out'));
    }, timeoutMs);

    globalThis.addEventListener('message', onMessage);
    iframe.src = silentLoginUrl.toString();
    document.body.appendChild(iframe);
  });

  return activeSsoCheck;
}

export async function getVerifiedAuthStatus(
  authServiceBaseUrl: string,
  retailUnit: string,
  lang: string,
  signal?: AbortSignal,
): Promise<GetAuth0SessionResponse> {
  if (getAppName() !== 'profile') {
    await checkSsoInIframe({ authServiceBaseUrl, retailUnit, lang });
  }

  const response = await fetch(new URL('status', `${authServiceBaseUrl.replace(/\/$/, '')}/`), {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Authentication status failed: ${response.status}`);
  }

  return response.json() as Promise<GetAuth0SessionResponse>;
}
